/**
 * Supplier Performance dashboard — how suppliers are delivering, from the
 * monthly files they upload. Rolling twelve months by default, filterable by
 * period, division and supplier, with target and trend lines on the charts and
 * supplier / division tables that drill the whole dashboard down on click.
 *
 * Rendered both as a Dashboard tab (embedded) and as its own page.
 */
import { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { AlertCircle, Info, Loader2, RefreshCw, X } from 'lucide-react';
import { useIsCustomer } from '../../../hooks/usePortalMode';
import PerformanceChart from './PerformanceChart';
import PerformanceDrilldownModal, { type DrilldownRequest } from './PerformanceDrilldownModal';
import PerformanceFilters from './PerformanceFilters';
import PerformanceKpiTiles from './PerformanceKpiTiles';
import { DivisionPerformanceTable, SupplierPerformanceTable } from './PerformanceTables';
import { useSupplierPerformance } from './useSupplierPerformance';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthName = (yyyyMm: string) => {
  const [y, m] = yyyyMm.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

interface SupplierPerformanceDashboardProps {
  /** Inside the Dashboard page: no page heading of its own. */
  embedded?: boolean;
}

export default function SupplierPerformanceDashboard({ embedded = false }: SupplierPerformanceDashboardProps) {
  const isCustomer = useIsCustomer();
  const { data, loading, error, filters, setFilters, resetFilters, reload } = useSupplierPerformance();
  const [drill, setDrill] = useState<DrilldownRequest | null>(null);
  const drillTo = (chartTitle: string, kind: DrilldownRequest['kind']) =>
    (month: { period: string }) => setDrill({ period: month.period, kind, chartTitle });

  const range = data?.range ?? null;
  const target = data?.target ?? { on_time: 95, otif: 95 };
  const months = data?.monthly ?? [];
  const divisionName = filters.division === 'unassigned'
    ? 'Unassigned'
    : data?.filters?.divisions.find((d) => String(d.id) === filters.division)?.name;
  const supplierName = data?.filters?.suppliers.find((s) => String(s.id) === filters.supplier)?.name;
  const priorLabel = range ? `previous ${range.months} months` : 'previous period';
  const coverage = data?.coverage;

  const header = !embedded && (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold">Supplier Performance</h1>
        <p className="text-sm text-muted-foreground">
          Delivery and quality from the monthly files suppliers send, scoped to your view.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>
  );

  if (error && !data) {
    return (
      <div className="space-y-4" data-testid="supplier-performance">
        {header}
        <Card data-testid="supplier-performance-error">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
            <Button variant="outline" size="sm" onClick={reload}>Try again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="space-y-4" data-testid="supplier-performance">
        {header}
        <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="supplier-performance-loading">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading supplier performance…
        </div>
      </div>
    );
  }

  if (data?.empty) {
    return (
      <div className="space-y-4" data-testid="supplier-performance">
        {header}
        <Card data-testid="supplier-performance-empty">
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No supplier performance uploads yet. Charts appear once suppliers send their monthly files.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="supplier-performance">
      {header}

      <PerformanceFilters
        filters={filters}
        onChange={setFilters}
        onReset={resetFilters}
        divisions={data?.filters?.divisions ?? []}
        suppliers={data?.filters?.suppliers ?? []}
        range={range ? { from: range.from, to: range.to } : null}
        hideSupplier={!isCustomer}
      />

      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="supplier-performance-scope">
        <span className="text-muted-foreground">
          {range ? `${monthName(range.from)} – ${monthName(range.to)} (${range.months} months)` : ''}
        </span>
        {divisionName && (
          <Button variant="secondary" size="sm" className="h-7" onClick={() => setFilters({ ...filters, division: '' })}>
            {divisionName} <X className="ml-1 h-3 w-3" />
          </Button>
        )}
        {supplierName && (
          <Button variant="secondary" size="sm" className="h-7" onClick={() => setFilters({ ...filters, supplier: '' })}>
            {supplierName} <X className="ml-1 h-3 w-3" />
          </Button>
        )}
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        {error && <span className="text-red-600">{error}</span>}
      </div>

      <PerformanceKpiTiles
        current={data?.kpis?.current ?? null}
        prior={data?.kpis?.prior ?? null}
        target={target}
        priorLabel={priorLabel}
        loading={loading}
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <PerformanceChart
          testId="supplier-performance-chart-on-time"
          title="Delivery on time %"
          onMonthClick={drillTo('Delivery on time %', 'delivery')}
          description="Delivered by the agreed date, within the line's allowance. Early counts as on time."
          months={months}
          series={[{ key: 'on_time', label: 'On time %', color: '#16a34a', kind: 'line' }]}
          percent target={target.on_time} trend
        />
        <PerformanceChart
          testId="supplier-performance-chart-otif"
          title="OTIF % (within the agreed window)"
          onMonthClick={drillTo('OTIF %', 'delivery')}
          description="The stricter reading: arriving too early misses the window as surely as arriving late."
          months={months}
          series={[{ key: 'otif', label: 'OTIF %', color: '#2563eb', kind: 'line' }]}
          percent target={target.otif} trend
        />
        <PerformanceChart
          testId="supplier-performance-chart-required"
          title="Required date and call-off"
          onMonthClick={drillTo('Required date and call-off', 'delivery')}
          description="Share of lines that met the date the site needed them, and call-offs made early enough for the SLA."
          months={months}
          series={[
            { key: 'required', label: 'Met required date %', color: '#9333ea', kind: 'line' },
            { key: 'call_off', label: 'Called off within SLA %', color: '#ea580c', kind: 'line' },
          ]}
          percent trend
        />
        <PerformanceChart
          testId="supplier-performance-chart-volume"
          title="Deliveries reported"
          onMonthClick={drillTo('Deliveries reported', 'delivery')}
          description="Delivery lines in the monthly files, with the three-month trend."
          months={months}
          series={[{ key: 'lines', label: 'Delivery lines', color: '#64748b', kind: 'bar' }]}
          trend
        />
        <PerformanceChart
          testId="supplier-performance-chart-quality"
          title="Quality: customer care cases"
          onMonthClick={drillTo('Quality', 'quality')}
          description="Customer care cases and NCRs reported on the monthly quality files."
          months={months}
          series={[
            { key: 'care_cases', label: 'Customer care cases', color: '#f59e0b', kind: 'bar' },
            { key: 'ncrs', label: 'NCRs', color: '#dc2626', kind: 'bar' },
          ]}
          trend
          note={coverage?.quality_ignores_division ? 'Quality files carry no division, so this chart shows all divisions.' : undefined}
        />
      </div>

      {coverage && coverage.lines > 0 && coverage.with_division < coverage.lines && !filters.division && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="supplier-performance-coverage">
          <Info className="h-3.5 w-3.5" />
          {coverage.with_division.toLocaleString()} of {coverage.lines.toLocaleString()} delivery lines in this period
          could be credited to a division from their PO site; the rest show as Unassigned.
        </p>
      )}

      {isCustomer && (
        <SupplierPerformanceTable
          rows={data?.suppliers ?? []}
          target={target.on_time}
          selectedId={filters.supplier}
          onSelect={(id) => setFilters({ ...filters, supplier: id })}
        />
      )}
      <PerformanceDrilldownModal
        request={drill}
        onClose={() => setDrill(null)}
        division={filters.division}
        supplier={filters.supplier}
        target={target.on_time}
        onPickSupplier={(id) => setFilters({ ...filters, supplier: id })}
      />
      <DivisionPerformanceTable
        rows={data?.divisions ?? []}
        target={target.on_time}
        selectedId={filters.division}
        onSelect={(id) => setFilters({ ...filters, division: id })}
      />
    </div>
  );
}
